import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  getAdminSettings,
  updateAdminSettings,
  sendDailyRemindersNow,
  uploadWhatsappTreeGreetingImage,
  uploadWhatsappTreeFollowupImage,
  resolveMediaUrl,
  superAdminResetAllAppointments,
  SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE,
  superAdminResetAllClinicalRecords,
  SUPER_RESET_CLINICAL_CONFIRM_PHRASE,
  getBookingPhoneStats,
  resetPhoneNoShowCount,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { toast } from 'sonner';
import {
  Ban,
  ClipboardList,
  FileText,
  MessageSquare,
  Save,
  Sparkles,
  Sun,
  Trash2,
} from 'lucide-react';
import { coerceBlacklistEntries, formatBlacklistPhone } from '../lib/waPhoneBlacklist';
import {
  clinicalFormTemplateOptions,
  DEFAULT_CLINICAL_FORM_TEMPLATE,
} from '../lib/clinicalFormTemplates';
import InvoicingSettingsCard from '../components/InvoicingSettingsCard';

function FlowStep({ step, title, hint, children }) {
  return (
    <section className="rounded-xl border border-border bg-card/50 p-4 space-y-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">{step}</p>
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {hint ? <p className="text-sm text-muted-foreground mt-1">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

function MenuNote({ children }) {
  return (
    <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-border bg-muted/20 px-3 py-2">
      {children}
    </p>
  );
}

const initialSettings = {
  wa_booking_whatsapp_enabled: false,
  wa_booking_whatsapp_template: '',
  wa_confirmation_whatsapp_enabled: true,
  wa_confirmation_whatsapp_template: '',
  appointment_reminder_enabled: true,
  appointment_reminder_minutes_before: 120,
  appointment_reminder_check_interval_sec: 60,
  public_base_url: 'http://localhost',
  appointment_reminder_template: '',
  appointment_daily_reminder_template: '',
  appointment_daily_reminder_time: '08:00',
  appointment_daily_attendance_deadline_time: '10:00',
  appointment_daily_auto_cancel_template: '',
  wa_tree_greeting_image_url: '',
  wa_tree_followup_image_url: '',
  wa_tree_greeting_text: '',
  wa_tree_menu_location: '',
  wa_tree_booking_confirm_template: '',
  wa_tree_booking_success_template: '',
  wa_tree_booking_success_review_template: '',
  wa_tree_booking_success_urgent_template: '',
  wa_phone_blacklist: [],
  wa_phone_whitelist: [],
  clinical_form_template: DEFAULT_CLINICAL_FORM_TEMPLATE,
  consent_podiatrist_name: '',
  consent_podiatrist_license: '',
  no_show_max_before_block: 2,
  booking_transfer_amount_label: '$300 MXN',
  booking_transfer_reminder_template:
    'Recuerda realizar tu transferencia por {amount} para confirmar tu cita.',
  booking_transfer_at_limit_template: 'Ahora el costo para agendar tu cita es de {amount}.',
  urgent_appointment_amount_label: '$500 MXN',
  wa_ai_enabled: false,
  wa_ai_base_url: 'https://generativelanguage.googleapis.com/v1beta/openai',
  wa_ai_model: 'gemini-2.5-flash',
  wa_ai_api_key: '',
  wa_ai_business_context: '',
};

const WA_AI_API_KEY_MASK = '********';

export default function AdminSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(initialSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingDaily, setSendingDaily] = useState(false);
  const [forceDailyResend, setForceDailyResend] = useState(false);
  const [uploadingWaGreeting, setUploadingWaGreeting] = useState(false);
  const [uploadingWaFollowup, setUploadingWaFollowup] = useState(false);
  const [resetAppointmentsBusy, setResetAppointmentsBusy] = useState(false);
  const [resetNoShowPhone, setResetNoShowPhone] = useState('');
  const [resetNoShowBusy, setResetNoShowBusy] = useState(false);
  const [noShowList, setNoShowList] = useState([]);
  const [noShowListLoading, setNoShowListLoading] = useState(false);
  const [noShowListError, setNoShowListError] = useState('');
  const [noShowAtLimitOnly, setNoShowAtLimitOnly] = useState(false);
  const [unblockingPhone, setUnblockingPhone] = useState(null);
  const [resetAppointmentsPhrase, setResetAppointmentsPhrase] = useState('');
  const [resetClinicalBusy, setResetClinicalBusy] = useState(false);
  const [resetClinicalPhrase, setResetClinicalPhrase] = useState('');
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getAdminSettings();
        setSettings({
          ...initialSettings,
          ...data,
          wa_phone_blacklist: coerceBlacklistEntries(data.wa_phone_blacklist),
          wa_phone_whitelist: coerceBlacklistEntries(data.wa_phone_whitelist),
        });
      } catch {
        toast.error('No se pudo cargar la configuración');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        appointment_reminder_minutes_before: Number(settings.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(settings.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (settings.appointment_daily_reminder_time || '').trim(),
        appointment_daily_attendance_deadline_time: (
          settings.appointment_daily_attendance_deadline_time || '10:00'
        ).trim(),
        no_show_max_before_block: Math.max(1, Number(settings.no_show_max_before_block) || 2),
      };
      const saved = await updateAdminSettings(payload);
      setSettings(saved);
      toast.success('Configuración guardada');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo guardar la configuración');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUploadWhatsappGreetingImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingWaGreeting(true);
    try {
      const { image_url: imageUrl } = await uploadWhatsappTreeGreetingImage(file);
      const next = { ...settings, wa_tree_greeting_image_url: imageUrl };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen de saludo WhatsApp guardada');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo subir la imagen');
    } finally {
      setUploadingWaGreeting(false);
      e.target.value = '';
    }
  };

  const handleClearWhatsappGreetingImage = async () => {
    setUploadingWaGreeting(true);
    try {
      const next = { ...settings, wa_tree_greeting_image_url: '' };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen de saludo quitada');
    } catch {
      toast.error('No se pudo quitar la imagen');
    } finally {
      setUploadingWaGreeting(false);
    }
  };

  const handleUploadWhatsappFollowupImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingWaFollowup(true);
    try {
      const { image_url: imageUrl } = await uploadWhatsappTreeFollowupImage(file);
      const next = { ...settings, wa_tree_followup_image_url: imageUrl };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen adicional de bienvenida guardada');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo subir la imagen');
    } finally {
      setUploadingWaFollowup(false);
      e.target.value = '';
    }
  };

  const handleClearWhatsappFollowupImage = async () => {
    setUploadingWaFollowup(true);
    try {
      const next = { ...settings, wa_tree_followup_image_url: '' };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen adicional quitada');
    } catch {
      toast.error('No se pudo quitar la imagen');
    } finally {
      setUploadingWaFollowup(false);
    }
  };

  const handleSendDailyReminders = async () => {
    setSendingDaily(true);
    try {
      const r = await sendDailyRemindersNow({ force: forceDailyResend });
      toast.success(
        `Recordatorios matutinos: ${r.sent} enviados` +
          (r.failed ? `, ${r.failed} fallidos` : '') +
          (r.skipped_no_phone ? `, ${r.skipped_no_phone} sin teléfono` : '')
      );
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo ejecutar el envío');
      }
    } finally {
      setSendingDaily(false);
    }
  };

  const handleResetAllAppointments = async () => {
    if (resetAppointmentsPhrase.trim() !== SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE) {
      toast.error('Escribe la frase de confirmación exacta.');
      return;
    }
    if (
      !window.confirm(
        'Se eliminarán todas las citas en MongoDB y la caché de reservas del worker WhatsApp. ¿Continuar?'
      )
    ) {
      return;
    }
    setResetAppointmentsBusy(true);
    try {
      const r = await superAdminResetAllAppointments(resetAppointmentsPhrase);
      setResetAppointmentsPhrase('');
      const parts = [`Eliminadas ${r.appointments_deleted ?? 0} citas en la base de datos.`];
      if (r.google_events_deleted > 0) {
        parts.push(`${r.google_events_deleted} evento(s) en Google Calendar.`);
      }
      if (r.booking_phone_stats_deleted > 0) {
        parts.push(`Inasistencias por teléfono: ${r.booking_phone_stats_deleted} registro(s) borrado(s).`);
      }
      if (r.whatsapp_cache_cleared && r.whatsapp_cache_keys_cleared > 0) {
        parts.push(
          r.whatsapp_full_reset
            ? `Historial WhatsApp (Redis): ${r.whatsapp_cache_keys_cleared} clave(s).`
            : `Caché citas WhatsApp: ${r.whatsapp_cache_keys_cleared} clave(s).`
        );
      }
      toast.success(parts.join(' '));
      if (r.google_calendar_note) {
        toast.info(String(r.google_calendar_note), { duration: 12000 });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo ejecutar el reset');
      }
    } finally {
      setResetAppointmentsBusy(false);
    }
  };

  const loadNoShowList = async () => {
    setNoShowListLoading(true);
    setNoShowListError('');
    try {
      const rows = await getBookingPhoneStats({
        minCount: 1,
        atLimitOnly: noShowAtLimitOnly,
      });
      setNoShowList(Array.isArray(rows) ? rows : []);
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const msg =
        status === 404
          ? 'El servidor aún no tiene la API del listado. Actualiza backend y frontend en el servidor.'
          : typeof detail === 'string'
            ? detail
            : 'No se pudo cargar el listado de inasistencias';
      setNoShowListError(msg);
      setNoShowList([]);
      toast.error(msg);
    } finally {
      setNoShowListLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      loadNoShowList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, noShowAtLimitOnly]);

  const noShowFlagLabel = (flag) => {
    if (flag === 'transfer_required') return 'En límite — transferencia';
    if (flag === 'no_show_warning') return 'Aviso previo';
    return 'Sin restricción';
  };

  const formatNoShowUpdatedAt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
      return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso.slice(0, 16);
    }
  };

  const handleUnblockNoShowPhone = async (phone10) => {
    setUnblockingPhone(phone10);
    try {
      const r = await resetPhoneNoShowCount(phone10);
      toast.success(r.message || 'Teléfono desbloqueado.');
      await loadNoShowList();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo desbloquear');
    } finally {
      setUnblockingPhone(null);
    }
  };

  const handleResetNoShowPhone = async () => {
    const digits = resetNoShowPhone.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Escribe el teléfono con al menos 10 dígitos.');
      return;
    }
    const phone10 = digits.slice(-10);
    setResetNoShowBusy(true);
    try {
      const r = await resetPhoneNoShowCount(phone10);
      setResetNoShowPhone('');
      toast.success(r.message || 'Inasistencias reiniciadas para ese teléfono.');
      await loadNoShowList();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo reiniciar el contador');
    } finally {
      setResetNoShowBusy(false);
    }
  };

  const handleResetAllClinicalRecords = async () => {
    if (resetClinicalPhrase.trim() !== SUPER_RESET_CLINICAL_CONFIRM_PHRASE) {
      toast.error('Escribe la frase de confirmación exacta.');
      return;
    }
    if (
      !window.confirm(
        'Se eliminarán TODOS los expedientes, historias clínicas, notas de evolución y fotos de evidencias. Las citas de la agenda NO se borran, solo se desvinculan. Esta acción no se puede deshacer. ¿Continuar?'
      )
    ) {
      return;
    }
    setResetClinicalBusy(true);
    try {
      const r = await superAdminResetAllClinicalRecords(resetClinicalPhrase);
      setResetClinicalPhrase('');
      toast.success(
        `Reset clínico: ${r.patient_records_deleted ?? 0} expedientes, ${r.clinical_encounters_deleted ?? 0} notas de evolución, ${r.attachment_files_deleted ?? 0} archivos.`
      );
      if (r.appointments_note) {
        toast.info(String(r.appointments_note), { duration: 12000 });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo ejecutar el reset clínico');
      }
    } finally {
      setResetClinicalBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Configura variables dinámicas del sistema</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              WhatsApp — Mensajes al paciente (orden del menú)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Plantillas en el mismo orden en que el paciente las ve en el bot: menú → agendar → confirmación del
              admin → recordatorios. Use <strong>*asteriscos*</strong> para negritas. Vacío = texto por defecto del
              servidor. El worker del puente debe alcanzar la <strong>URL pública base</strong> (al final de esta
              tarjeta).
            </p>

            <FlowStep
              step="Paso 0 — Menú principal"
              title="Bienvenida e imágenes"
              hint="Al escribir 0, hola o al iniciar el chat. Opciones 1️⃣ Agendar, 2️⃣ Consultar horarios y 3️⃣ Cancelar (si hay cita)."
            >
              <div className="space-y-2">
                <Label htmlFor="wa_tree_greeting_text">Texto de bienvenida</Label>
                <Textarea
                  id="wa_tree_greeting_text"
                  rows={5}
                  value={settings.wa_tree_greeting_text ?? ''}
                  onChange={(e) => handleChange('wa_tree_greeting_text', e.target.value)}
                  placeholder={'🌸 Hola, bienvenido(a) a Centro Cultural 🌸\n\nSerá un gusto ayudarte 😊'}
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Este párrafo aparece antes del menú 1️⃣ Agendar / 2️⃣ Horarios. El bot lo consulta desde
                  Ajustes (no está fijo en el servidor).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wa_tree_menu_location">Ubicación en el menú</Label>
                <Input
                  id="wa_tree_menu_location"
                  value={settings.wa_tree_menu_location ?? ''}
                  onChange={(e) => handleChange('wa_tree_menu_location', e.target.value)}
                  placeholder="📍 Cd. Sahagún, Hgo."
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Línea bajo las opciones del menú. Vacío = texto por defecto del servidor.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Imagen de logo (menú como pie de foto)</Label>
              {settings.wa_tree_greeting_image_url ? (
                <div className="rounded-lg border border-border overflow-hidden max-w-xs bg-muted/30">
                  <img
                    src={resolveMediaUrl(settings.wa_tree_greeting_image_url)}
                    alt="Vista previa logo saludo WhatsApp"
                    className="w-full h-auto object-contain max-h-40"
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin imagen de logo.</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="max-w-sm cursor-pointer"
                  disabled={uploadingWaGreeting}
                  onChange={handleUploadWhatsappGreetingImage}
                />
                {settings.wa_tree_greeting_image_url ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingWaGreeting}
                    onClick={handleClearWhatsappGreetingImage}
                  >
                    Quitar logo
                  </Button>
                ) : null}
              </div>
            </div>
              <div className="space-y-2">
                <Label>Imagen adicional tras la bienvenida</Label>
                <p className="text-xs text-muted-foreground">
                  Segundo mensaje justo después del saludo (solo imagen).
                </p>
              {settings.wa_tree_followup_image_url ? (
                <div className="rounded-lg border border-border overflow-hidden max-w-xs bg-muted/30">
                  <img
                    src={resolveMediaUrl(settings.wa_tree_followup_image_url)}
                    alt="Vista previa imagen adicional WhatsApp"
                    className="w-full h-auto object-contain max-h-48"
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin imagen adicional.</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="max-w-sm cursor-pointer"
                  disabled={uploadingWaFollowup}
                  onChange={handleUploadWhatsappFollowupImage}
                />
                {settings.wa_tree_followup_image_url ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingWaFollowup}
                    onClick={handleClearWhatsappFollowupImage}
                  >
                    Quitar imagen adicional
                  </Button>
                ) : null}
              </div>
              </div>
            </FlowStep>

            <FlowStep
              step="Paso 0b — Aviso en el saludo"
              title="Inasistencias y transferencia"
              hint="Se añade al texto del menú si el teléfono tiene «No llegó paciente» en Citas."
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="booking_transfer_amount_label">Monto de transferencia</Label>
                  <Input
                    id="booking_transfer_amount_label"
                    className="mt-1 h-11"
                    value={settings.booking_transfer_amount_label ?? ''}
                    onChange={(e) => handleChange('booking_transfer_amount_label', e.target.value)}
                    placeholder="$300 MXN"
                  />
                </div>
                <div>
                  <Label htmlFor="no_show_max_before_block">Inasistencias antes del aviso de costo</Label>
                  <Input
                    id="no_show_max_before_block"
                    type="number"
                    min={1}
                    max={50}
                    className="mt-1 h-11 max-w-[160px]"
                    value={settings.no_show_max_before_block ?? 2}
                    onChange={(e) => handleChange('no_show_max_before_block', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="booking_transfer_reminder_template">
                  Antes del límite (use {'{amount}'})
                </Label>
                <Textarea
                  id="booking_transfer_reminder_template"
                  rows={2}
                  className="mt-1 font-mono text-sm"
                  value={settings.booking_transfer_reminder_template ?? ''}
                  onChange={(e) => handleChange('booking_transfer_reminder_template', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="booking_transfer_at_limit_template">
                  Al alcanzar el límite (use {'{amount}'})
                </Label>
                <Textarea
                  id="booking_transfer_at_limit_template"
                  rows={2}
                  className="mt-1 font-mono text-sm"
                  value={settings.booking_transfer_at_limit_template ?? ''}
                  onChange={(e) => handleChange('booking_transfer_at_limit_template', e.target.value)}
                />
              </div>
            </FlowStep>

            <FlowStep
              step="Paso 1 — Opción 1️⃣ Agendar cita"
              title="Resumen para confirmar"
              hint="Tras elegir fecha y hora; el paciente ve este resumen antes de pulsar 1."
            >
              <Textarea
                id="wa_tree_booking_confirm_template"
                rows={8}
                value={settings.wa_tree_booking_confirm_template ?? ''}
                onChange={(e) => handleChange('wa_tree_booking_confirm_template', e.target.value)}
                placeholder={
                  '✨ Confirma tu {kind}:\n\n' +
                  '👤 {patient_name}\n' +
                  '👣 Servicio: {service}\n' +
                  '📅 {day}\n' +
                  '🕒 {time}{cost_block}\n\n' +
                  'Responde:\n\n' +
                  '✅ *1* para confirmar\n' +
                  '❌ *0* para cancelar{booking_notice_block}'
                }
                className="font-mono text-sm"
              />
              <MenuNote>
                Variables: {'{kind}'}, {'{patient_name}'}, {'{service}'}, {'{day}'}, {'{time}'},{' '}
                {'{cost_block}'} (cita urgente), {'{booking_notice_block}'} (transferencia por inasistencias).
              </MenuNote>
            </FlowStep>

            <FlowStep
              step="Paso 1 — Opción 1️⃣ Agendar cita"
              title="Solicitud registrada"
              hint="Tras pulsar 1 en el resumen anterior."
            >
              <Textarea
                id="wa_tree_booking_success_template"
                rows={5}
                value={settings.wa_tree_booking_success_template ?? ''}
                onChange={(e) => handleChange('wa_tree_booking_success_template', e.target.value)}
                placeholder={
                  '🌸 ¡Listo! Tu solicitud fue enviada correctamente 😊\n\n' +
                  'En breve recibirás la confirmación de tu cita por este medio.\n\n' +
                  'Gracias por confiar en *Centro Cultural* 👣'
                }
                className="font-mono text-sm"
              />
            </FlowStep>

            <FlowStep
              step="Paso 2 — Opción 2️⃣ Consultar horarios"
              title="Sin plantilla fija"
            >
              <MenuNote>
                El bot pide el tipo de servicio y muestra horarios dinámicos desde la agenda (no hay texto editable
                aquí). Configura tipos de cita y horarios en el panel de Tipos de cita.
              </MenuNote>
            </FlowStep>

            <FlowStep
              step="Paso 3 — Opción 3️⃣ Cambiar o cancelar"
              title="Solo si hay cita agendada"
            >
              <MenuNote>
                El menú lista las citas del número y permite cancelar o reagendar. No requiere plantilla en Settings.
              </MenuNote>
            </FlowStep>

            <FlowStep
              step="Paso 4 — Opción 4️⃣ Revisión breve (opcional)"
              title="Solicitud de revisión"
              hint="Solo si WA_MENU_LEGACY_FLOWS_ENABLED=1 en el puente WhatsApp."
            >
              <Textarea
                id="wa_tree_booking_success_review_template"
                rows={4}
                value={settings.wa_tree_booking_success_review_template ?? ''}
                onChange={(e) => handleChange('wa_tree_booking_success_review_template', e.target.value)}
                placeholder="Vacío = texto por defecto del servidor."
                className="font-mono text-sm"
              />
            </FlowStep>

            <FlowStep
              step="Paso 5 — Opción 5️⃣ Cita urgente (opcional)"
              title="Solicitud urgente"
              hint="Solo si WA_MENU_LEGACY_FLOWS_ENABLED=1 en el puente WhatsApp."
            >
              <div>
                <Label htmlFor="urgent_appointment_amount_label">Costo informativo en el flujo</Label>
                <Input
                  id="urgent_appointment_amount_label"
                  className="mt-1 h-11 max-w-xs"
                  value={settings.urgent_appointment_amount_label ?? ''}
                  onChange={(e) => handleChange('urgent_appointment_amount_label', e.target.value)}
                  placeholder="$500 MXN"
                />
              </div>
              <Textarea
                id="wa_tree_booking_success_urgent_template"
                rows={4}
                value={settings.wa_tree_booking_success_urgent_template ?? ''}
                onChange={(e) => handleChange('wa_tree_booking_success_urgent_template', e.target.value)}
                placeholder="Vacío = texto por defecto del servidor."
                className="font-mono text-sm"
              />
            </FlowStep>

            <FlowStep
              step="Paso 6 — Confirmación del administrador"
              title="Cita aprobada en el panel"
              hint="Cuando pulsas Confirmar en Citas (no al registrar la solicitud en el bot)."
            >
              <label className="flex items-center gap-3 p-3 bg-sage-50 rounded-lg cursor-pointer">
                <Checkbox
                  checked={settings.wa_confirmation_whatsapp_enabled}
                  onCheckedChange={(checked) =>
                    handleChange('wa_confirmation_whatsapp_enabled', Boolean(checked))
                  }
                />
                <span className="text-sm">Enviar WhatsApp al confirmar la cita</span>
              </label>
              <Textarea
                id="confirmation-template"
                rows={6}
                value={settings.wa_confirmation_whatsapp_template}
                onChange={(e) => handleChange('wa_confirmation_whatsapp_template', e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Placeholders: {'{patient_name}'}, {'{date}'}, {'{time}'}, {'{reason}'}, {'{reason_line}'}.
              </p>
            </FlowStep>

            <FlowStep
              step="Paso 7 — Recordatorio matutino"
              title="Citas del día (mismo día)"
              hint="Envío automático a la hora configurada; el paciente responde 1 o SÍ."
            >
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="daily-reminder-time"
                  type="time"
                  value={settings.appointment_daily_reminder_time || ''}
                  onChange={(e) => handleChange('appointment_daily_reminder_time', e.target.value)}
                  className="h-11 max-w-[220px]"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-11 rounded-full"
                  onClick={() => handleChange('appointment_daily_reminder_time', '')}
                >
                  Desactivar envío diario
                </Button>
              </div>
              <Textarea
                id="daily-reminder-template"
                rows={5}
                value={settings.appointment_daily_reminder_template}
                onChange={(e) => handleChange('appointment_daily_reminder_template', e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Placeholders: {'{patient_name}'}, {'{date}'}, {'{time}'}.
              </p>
            </FlowStep>

            <FlowStep
              step="Paso 8 — Sin confirmar asistencia"
              title="Cancelación automática"
              hint="Si no confirma antes de la hora límite tras el recordatorio matutino."
            >
              <div>
                <Label htmlFor="daily-attendance-deadline-time">Hora límite de confirmación</Label>
                <Input
                  id="daily-attendance-deadline-time"
                  type="time"
                  value={settings.appointment_daily_attendance_deadline_time || '10:00'}
                  onChange={(e) =>
                    handleChange('appointment_daily_attendance_deadline_time', e.target.value)
                  }
                  className="h-11 max-w-[220px] mt-1"
                />
              </div>
              <Textarea
                id="daily-auto-cancel-template"
                rows={4}
                value={settings.appointment_daily_auto_cancel_template ?? ''}
                onChange={(e) =>
                  handleChange('appointment_daily_auto_cancel_template', e.target.value)
                }
                className="font-mono text-sm"
              />
            </FlowStep>

            <FlowStep
              step="Paso 9 — Recordatorio por anticipación"
              title="Antes de la hora de la cita"
            >
              <label className="flex items-center gap-3 p-3 bg-sage-50 rounded-lg cursor-pointer">
                <Checkbox
                  checked={settings.appointment_reminder_enabled}
                  onCheckedChange={(checked) => handleChange('appointment_reminder_enabled', Boolean(checked))}
                />
                <span className="text-sm">Activar recordatorios automáticos</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reminder-minutes">Minutos antes de la cita</Label>
                  <Input
                    id="reminder-minutes"
                    type="number"
                    min="1"
                    max="10080"
                    value={settings.appointment_reminder_minutes_before}
                    onChange={(e) => handleChange('appointment_reminder_minutes_before', e.target.value)}
                    className="h-11 mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="reminder-interval">Revisión cada (segundos)</Label>
                  <Input
                    id="reminder-interval"
                    type="number"
                    min="15"
                    max="3600"
                    value={settings.appointment_reminder_check_interval_sec}
                    onChange={(e) => handleChange('appointment_reminder_check_interval_sec', e.target.value)}
                    className="h-11 mt-1"
                  />
                </div>
              </div>
              <Textarea
                id="reminder-template"
                rows={6}
                value={settings.appointment_reminder_template}
                onChange={(e) => handleChange('appointment_reminder_template', e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Placeholders: {'{patient_name}'}, {'{date}'}, {'{time}'}, {'{confirm_url}'}, {'{cancel_url}'},{' '}
                {'{reschedule_url}'}.
              </p>
            </FlowStep>

            <div className="rounded-lg border border-border p-4 space-y-2">
              <Label htmlFor="public-base-url">URL pública base (enlaces en recordatorios)</Label>
              <Input
                id="public-base-url"
                value={settings.public_base_url}
                onChange={(e) => handleChange('public_base_url', e.target.value)}
                className="h-11"
                placeholder="https://tudominio.com"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Recepción con IA (WhatsApp)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Activa una recepción conversacional con Inteligencia Artificial para agendar citas por WhatsApp de forma
              natural. Cuando está <strong>apagada</strong>, el bot funciona con el menú determinista de siempre (los
              pasos de arriba). La IA solo se usa si está encendida <strong>y</strong> hay un API key configurado.
            </p>

            <FlowStep
              step="Encendido"
              title="Activar recepción con IA"
              hint="Al activarla, la IA conduce la conversación de citas. Si la IA falla, el bot vuelve automáticamente al menú clásico."
            >
              <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg cursor-pointer">
                <Checkbox
                  checked={Boolean(settings.wa_ai_enabled)}
                  onCheckedChange={(checked) => handleChange('wa_ai_enabled', Boolean(checked))}
                />
                <span className="text-sm">Encender la recepción con IA en WhatsApp</span>
              </label>
            </FlowStep>

            <FlowStep
              step="Credenciales"
              title="Proveedor de IA y API key"
              hint="Por defecto Gemini (endpoint compatible con OpenAI). Puedes usar otro proveedor compatible cambiando la URL base y el modelo."
            >
              <div className="space-y-2">
                <Label htmlFor="wa_ai_api_key">API key</Label>
                <Input
                  id="wa_ai_api_key"
                  type="password"
                  autoComplete="new-password"
                  value={settings.wa_ai_api_key ?? ''}
                  onChange={(e) => handleChange('wa_ai_api_key', e.target.value)}
                  className="h-11 font-mono text-sm"
                  placeholder={
                    settings.wa_ai_api_key === WA_AI_API_KEY_MASK
                      ? 'API key guardada (déjala así para no cambiarla)'
                      : 'Pega aquí tu API key'
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Por seguridad, la key no se muestra completa. Para conservarla, no edites este campo; para
                  cambiarla, escribe la nueva.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="wa_ai_model">Modelo</Label>
                  <Input
                    id="wa_ai_model"
                    value={settings.wa_ai_model ?? ''}
                    onChange={(e) => handleChange('wa_ai_model', e.target.value)}
                    className="h-11"
                    placeholder="gemini-2.5-flash"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wa_ai_base_url">URL base (compatible OpenAI)</Label>
                  <Input
                    id="wa_ai_base_url"
                    value={settings.wa_ai_base_url ?? ''}
                    onChange={(e) => handleChange('wa_ai_base_url', e.target.value)}
                    className="h-11 font-mono text-sm"
                    placeholder="https://generativelanguage.googleapis.com/v1beta/openai"
                  />
                </div>
              </div>
            </FlowStep>

            <FlowStep
              step="Alcance"
              title="Rol y límites de la IA"
              hint="Describe el rol del negocio y qué NO debe responder. Esto evita que gaste respondiendo temas ajenos a la clínica."
            >
              <div className="space-y-2">
                <Label htmlFor="wa_ai_business_context">Contexto del negocio / instrucciones</Label>
                <Textarea
                  id="wa_ai_business_context"
                  rows={7}
                  value={settings.wa_ai_business_context ?? ''}
                  onChange={(e) => handleChange('wa_ai_business_context', e.target.value)}
                  className="mt-1 font-mono text-sm"
                  placeholder={
                    'Eres la recepción virtual de una clínica de rehabilitación física. Ayuda solo a agendar, ' +
                    'consultar, reagendar o cancelar citas e informar sobre servicios y horarios. Si preguntan algo ' +
                    'ajeno a la clínica, declina amablemente y reconduce al agendado.'
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Vacío = se usa el texto por defecto del servidor (clínica de rehabilitación física).
                </p>
              </div>
            </FlowStep>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-primary" />
              Inasistencias — gestión operativa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Las plantillas de aviso están en <strong>Paso 0b</strong> arriba. Aquí gestionas contadores y el listado
              de teléfonos con inasistencias.
            </p>
            <div>
              <Label htmlFor="reset-no-show-phone">Reiniciar contador de inasistencias (teléfono)</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                <Input
                  id="reset-no-show-phone"
                  className="h-11 max-w-xs"
                  placeholder="10 dígitos, ej. 8115556484"
                  value={resetNoShowPhone}
                  onChange={(e) => setResetNoShowPhone(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="h-11 rounded-full"
                  disabled={resetNoShowBusy}
                  onClick={handleResetNoShowPhone}
                >
                  {resetNoShowBusy ? 'Reiniciando…' : 'Reiniciar contador'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Quita el aviso de transferencia en WhatsApp hasta que vuelva a marcarse «No llegó paciente».
              </p>
            </div>

            <div
              id="listado-inasistencias"
              className="rounded-xl border-2 border-primary/25 bg-primary/5 p-4 space-y-3 scroll-mt-24"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-foreground">Listado de teléfonos con inasistencias</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Números con «No llegó paciente» en Citas. Desbloquear quita el aviso de transferencia en WhatsApp.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Checkbox
                    id="no-show-at-limit-only"
                    checked={noShowAtLimitOnly}
                    onCheckedChange={(v) => setNoShowAtLimitOnly(v === true)}
                  />
                  <Label htmlFor="no-show-at-limit-only" className="text-sm font-normal cursor-pointer">
                    Solo en límite de transferencia
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    disabled={noShowListLoading}
                    onClick={loadNoShowList}
                  >
                    {noShowListLoading ? 'Cargando…' : 'Actualizar'}
                  </Button>
                </div>
              </div>
              {noShowListError ? (
                <p className="text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                  {noShowListError}
                </p>
              ) : null}
              {noShowListLoading && noShowList.length === 0 && !noShowListError ? (
                <p className="text-sm text-muted-foreground">Cargando listado…</p>
              ) : noShowList.length === 0 && !noShowListError ? (
                <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-border px-4 py-6 text-center bg-background">
                  No hay teléfonos con inasistencias registradas
                  {noShowAtLimitOnly ? ' en el límite configurado' : ''}. Marca «No llegó paciente» en una cita para
                  que aparezcan aquí.
                </p>
              ) : noShowList.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-border bg-background">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-left">
                        <th className="px-3 py-2 font-medium">Teléfono</th>
                        <th className="px-3 py-2 font-medium">Inasistencias</th>
                        <th className="px-3 py-2 font-medium">Estado</th>
                        <th className="px-3 py-2 font-medium">Actualizado</th>
                        <th className="px-3 py-2 font-medium text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {noShowList.map((row) => (
                        <tr key={row.phone_digits_10} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 font-mono">{formatBlacklistPhone(row.phone_digits_10)}</td>
                          <td className="px-3 py-2">{row.no_show_count}</td>
                          <td className="px-3 py-2">
                            <span
                              className={
                                row.booking_flag === 'transfer_required'
                                  ? 'text-amber-700 dark:text-amber-400 font-medium'
                                  : ''
                              }
                            >
                              {noShowFlagLabel(row.booking_flag)}
                            </span>
                            {row.in_wa_blacklist ? (
                              <span className="block text-xs text-destructive mt-0.5">
                                También en lista negra WhatsApp (menú WhatsApp)
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                            {formatNoShowUpdatedAt(row.updated_at)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="rounded-full"
                              disabled={unblockingPhone === row.phone_digits_10}
                              onClick={() => handleUnblockNoShowPhone(row.phone_digits_10)}
                            >
                              {unblockingPhone === row.phone_digits_10 ? '…' : 'Desbloquear'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {user && isSuperAdmin(user.role) ? (
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                Historia clínica — rubro / formulario activo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Solo super-admin. Todos los expedientes en borrador usan este formulario. Las historias ya
                finalizadas conservan el formato con el que se completaron.
              </p>
              <div className="space-y-2">
                {clinicalFormTemplateOptions().map((opt) => {
                  const selected = (settings.clinical_form_template || DEFAULT_CLINICAL_FORM_TEMPLATE) === opt.id;
                  return (
                    <label
                      key={opt.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="clinical_form_template"
                        className="mt-1"
                        checked={selected}
                        onChange={() => handleChange('clinical_form_template', opt.id)}
                      />
                      <span>
                        <span className="font-medium text-foreground">{opt.label}</span>
                        <span className="block text-sm text-muted-foreground mt-0.5">{opt.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Historia clínica — médico responsable
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Estos datos se insertan automáticamente en el consentimiento (cuando aplica) y en la plantilla PDF
              de las notas de seguimiento / evolución de cualquier rubro.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="consent_podiatrist_name">Nombre del médico(a)</Label>
                <Input
                  id="consent_podiatrist_name"
                  className="mt-1 h-11"
                  value={settings.consent_podiatrist_name ?? ''}
                  onChange={(e) => handleChange('consent_podiatrist_name', e.target.value)}
                  placeholder="Ej. Dra. María García"
                />
              </div>
              <div>
                <Label htmlFor="consent_podiatrist_license">Cédula profesional (opcional)</Label>
                <Input
                  id="consent_podiatrist_license"
                  className="mt-1 h-11"
                  value={settings.consent_podiatrist_license ?? ''}
                  onChange={(e) => handleChange('consent_podiatrist_license', e.target.value)}
                  placeholder="Ej. cédula profesional o registro interno"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <InvoicingSettingsCard />

        {user && isSuperAdmin(user.role) ? (
          <Card className="border border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-900">
                <Trash2 className="w-5 h-5 shrink-0" />
                Super administrador — operaciones destructivas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Acciones irreversibles. Cada una requiere la frase exacta indicada. Orden sugerido: primero citas si
                quieres vaciar agenda; historias clínicas no borra citas.
              </p>

              <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Sun className="w-4 h-4 shrink-0" />
                  1. Recordatorios matutinos (manual)
                </p>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={forceDailyResend}
                    onCheckedChange={(c) => setForceDailyResend(Boolean(c))}
                  />
                  <span>Reenviar a todos los de hoy aunque ya se haya enviado el matutino</span>
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  className="rounded-full gap-2"
                  disabled={sendingDaily}
                  onClick={handleSendDailyReminders}
                >
                  <Sun className="w-4 h-4" />
                  {sendingDaily ? 'Enviando…' : 'Enviar recordatorios matutinos ahora'}
                </Button>
              </div>

              <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 space-y-3">
                <p className="text-sm font-semibold text-red-900 flex items-center gap-2">
                  <Trash2 className="w-4 h-4 shrink-0" />
                  2. Borrar todas las citas
                </p>
                <p className="text-xs text-red-900/90 leading-relaxed">
                  MongoDB (todas las citas), contadores de inasistencia, caché del bot WhatsApp en Redis. No elimina
                  usuarios, productos, reseñas ni esta configuración.
                </p>
                <div>
                  <Label htmlFor="reset-appointments-phrase" className="text-red-900">
                    Frase: <code className="text-xs">{SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE}</code>
                  </Label>
                  <Input
                    id="reset-appointments-phrase"
                    value={resetAppointmentsPhrase}
                    onChange={(e) => setResetAppointmentsPhrase(e.target.value)}
                    className="mt-1 font-mono text-sm bg-white border-red-200"
                    placeholder={SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE}
                    autoComplete="off"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  className="rounded-full gap-2"
                  disabled={
                    resetAppointmentsBusy ||
                    resetAppointmentsPhrase.trim() !== SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE
                  }
                  onClick={handleResetAllAppointments}
                >
                  <Trash2 className="w-4 h-4" />
                  {resetAppointmentsBusy ? 'Eliminando…' : 'Eliminar todas las citas'}
                </Button>
              </div>

              <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 space-y-3">
                <p className="text-sm font-semibold text-red-900 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 shrink-0" />
                  3. Borrar todas las historias clínicas
                </p>
                <p className="text-xs text-red-900/90 leading-relaxed">
                  Expedientes, historias, notas de evolución y fotos. Reinicia folios HC- y NE-. Las citas permanecen
                  (solo se desvinculan del expediente).
                </p>
                <div>
                  <Label htmlFor="reset-clinical-phrase" className="text-red-900">
                    Frase: <code className="text-xs">{SUPER_RESET_CLINICAL_CONFIRM_PHRASE}</code>
                  </Label>
                  <Input
                    id="reset-clinical-phrase"
                    value={resetClinicalPhrase}
                    onChange={(e) => setResetClinicalPhrase(e.target.value)}
                    className="mt-1 font-mono text-sm bg-white border-red-200"
                    placeholder={SUPER_RESET_CLINICAL_CONFIRM_PHRASE}
                    autoComplete="off"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  className="rounded-full gap-2"
                  disabled={
                    resetClinicalBusy ||
                    resetClinicalPhrase.trim() !== SUPER_RESET_CLINICAL_CONFIRM_PHRASE
                  }
                  onClick={handleResetAllClinicalRecords}
                >
                  <Trash2 className="w-4 h-4" />
                  {resetClinicalBusy ? 'Eliminando…' : 'Eliminar todas las historias clínicas'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Button onClick={handleSave} className="w-full rounded-full gap-2 h-12" disabled={saving}>
          <Save className="w-4 h-4" />
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      </div>
    </div>
  );
}
